import { range, sum, take } from 'lodash';
import { TournamentBreakdown, TournamentDistributionType } from '@okletsplay/core';
import { useMemo } from 'react';
import { divide, multiply } from 'shared/utils';

interface BreakdownSettings {
  totalPrize: number;
  manualWeights: number[];
  places: number[][];
  ranks: number[];
  distributionType: TournamentDistributionType;
  maxTeams: number;
}

export const generateDistributionBreakdown = ({
  totalPrize,
  manualWeights,
  places,
  ranks,
  distributionType,
  maxTeams,
}: BreakdownSettings): TournamentBreakdown[] => {
  let breakdown: TournamentBreakdown[] = [];
  const payedPlacesCount = places.length;

  switch (distributionType) {
    case TournamentDistributionType.EqualDivision:
      breakdown = range(0, payedPlacesCount).map((index) => ({
        places: places[index],
        amount: divide(totalPrize, Math.max(...places?.flatMap((place) => place ?? []))),
      }));
      break;
    case TournamentDistributionType.AutomaticTiers:
      const lastPositionPrize = divide(totalPrize, sum(ranks));

      breakdown = range(0, places.length).map((index) => {
        return {
          places: places[index],
          amount: divide(multiply(lastPositionPrize, ranks[index]), places[index]?.length ?? 1),
        };
      });
      break;
    case TournamentDistributionType.ManualWeights:
      const manualWeightsSum = take(manualWeights, places.length).reduce(
        (total, weight, index) => total + (weight ?? 1) * places[index]?.length ?? 1,
        0
      );

      const manualRankPrize = divide(totalPrize, manualWeightsSum);

      if (manualRankPrize === 0) {
        return [
          {
            places: range(1, maxTeams + 1),
            amount: 0,
          },
        ];
      }

      breakdown = (manualWeights ?? [])
        .map((rank, index) => ({
          places: places[index],
          amount: multiply(manualRankPrize, rank ?? 1),
        }))
        .filter((breakdownItem) => breakdownItem.places?.length);
      break;
  }

  if (places?.length && !places?.[places?.length - 1]?.includes(maxTeams)) {
    breakdown.push({
      places: range(Math.max(...places[places.length - 1]) + 1, maxTeams + 1),
      amount: 0,
    });
  }

  return breakdown;
};

export const useDistributionBreakdown = ({
  totalPrize,
  manualWeights,
  places,
  ranks,
  distributionType,
  maxTeams,
}: BreakdownSettings) => {
  const distributionBreakdown = useMemo(
    () =>
      generateDistributionBreakdown({
        totalPrize,
        manualWeights,
        places,
        ranks,
        distributionType,
        maxTeams,
      }),
    [places, distributionType, maxTeams, manualWeights, totalPrize, ranks]
  );

  return {
    distributionBreakdown,
  };
};
